import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import InstallMobileRoundedIcon from '@mui/icons-material/InstallMobileRounded';
import { SvgIconComponent } from '@mui/icons-material';
import config from 'config';
import type { AppliedJobsSummaryResponse } from 'src/openapi';
import { DashboardPersona } from './persona';

export enum EarnCreditGoalKey {
  ProfilePicture = 'profile_picture',
  FirstJobApplication = 'first_job_application',
  CourseLessons = 'course_lessons',
  FirstReferral = 'first_referral',
  DownloadMobileApp = 'download_mobile_app',
}

export const EARN_CREDIT_GOAL_DATA: Record<
  string,
  {
    title: string;
    description: string;
    Icon: SvgIconComponent;
    route?: string;
    // External destination (opened in a new tab) — used when there is no in-app route.
    href?: string;
  }
> = {
  [EarnCreditGoalKey.ProfilePicture]: {
    title: 'Upload your profile picture',
    description: 'Add a photo so recruiters recognise you.',
    Icon: PhotoCameraRoundedIcon,
    route: '/profile',
  },
  [EarnCreditGoalKey.FirstJobApplication]: {
    title: 'Apply to your first job or internship',
    description: 'Take the first step towards your next role.',
    Icon: WorkOutlineRoundedIcon,
    route: '/job-search',
  },
  [EarnCreditGoalKey.CourseLessons]: {
    title: 'Complete 3 course lessons',
    description: 'Work through lessons in a guided course.',
    Icon: MenuBookRoundedIcon,
    route: '/course',
  },
  [EarnCreditGoalKey.FirstReferral]: {
    title: 'Refer your first friend',
    description: 'Invite a friend and grow together on Abekus.',
    Icon: GroupAddRoundedIcon,
    route: '/user/referral',
  },
  [EarnCreditGoalKey.DownloadMobileApp]: {
    title: 'Download the mobile app',
    description: 'Learn and play on the go with the Abekus app.',
    Icon: InstallMobileRoundedIcon,
    href: `${config.JOBS_BASE_URL}/mobile-app`,
  },
};

// WhatsApp number prompt. Never on a brand-new account: signup just stopped
// asking for the number, and asking again on the first dashboard visit would
// only move the friction one screen later.
export const WHATSAPP_PROMPT_MIN_ACCOUNT_AGE_DAYS = 3;
export const WHATSAPP_PROMPT_DELAY_MS = 20_000;
export const WHATSAPP_PROMPT_SNOOZE_DAYS = 7;
export const WHATSAPP_PROMPT_MAX_DISMISSALS = 3;

/** The application pipeline's stages, in order: blue while it is the
 *  candidate's move, gold once a recruiter has acted. */
export const APPLICATION_STAGES: {
  key: keyof AppliedJobsSummaryResponse;
  label: string;
  tone: 'blue' | 'gold';
}[] = [
  { key: 'applied', label: 'Applied', tone: 'blue' },
  { key: 'shortlisted', label: 'Shortlisted', tone: 'blue' },
  { key: 'interview', label: 'Interview', tone: 'gold' },
  { key: 'offer', label: 'Offer', tone: 'gold' },
];

/** The jobs card's heading per persona. Experienced has no jobs card. */
export const JOBS_CARD_COPY: Record<
  DashboardPersona,
  { title: string; note: string }
> = {
  [DashboardPersona.Learner]: {
    title: 'Jobs for you',
    note: 'Matched to your skills and resume',
  },
  [DashboardPersona.JobFinder]: {
    title: 'Jobs matched to your resume',
    note: 'Best matches first',
  },
  [DashboardPersona.Experienced]: {
    title: 'Roles for you',
    note: 'Matched to your experience',
  },
};
